import { type WrappedInsight } from "@clipboard-health/contract-backend-main";

import {
  AnimatedInsightWrapper,
  type AnimatedInsightWrapperProps,
} from "../AnimatedInsightWrapper";
import { AllHoursChampionContent } from "./AllHoursChampion/Content";
import { CommunityReviewsContent } from "./CommunityReviews/Content";
import { DistanceAndCommuteContent } from "./DistanceAndCommute/Content";
import { EveningAllStarContent } from "./EveningAllStar/Content";
import { HeroMarketContextContent } from "./HeroMarketContext/Content";
import { MorningMvpContent } from "./MorningMvp/Content";
import { NightShiftLegendContent } from "./NightShiftLegend/Content";
import { PlanningAheadContent } from "./PlanningAhead/Content";
import { ShiftVolumeContent } from "./ShiftVolume/Content";
import { SummaryContent } from "./Summary/Content";
import { TeamAndConnectionsContent } from "./TeamAndConnections/Content";
import { TopWorkplacesContent } from "./TopWorkplaces/Content";
import { TotalEarningsContent } from "./TotalEarnings/Content";
import { TotalEarningsLifetimeContent } from "./TotalEarningsLifetime/Content";
import { UrgentChampionContent } from "./UrgentChampion/Content";
import { WorkerShiftTypeDiversityContent } from "./WorkerShiftTypeDiversity/Content";

interface KnownInsightContentContainerProps
  extends Pick<AnimatedInsightWrapperProps, "onClick" | "visible"> {
  insight: WrappedInsight;
  isPreview?: boolean;
}

export function KnownInsightContentContainer(props: KnownInsightContentContainerProps) {
  const { insight, visible, onClick, isPreview = false } = props;

  const getContent = () => {
    const { attributes } = insight;

    switch (attributes.insightType) {
      case "hero-market-context": {
        return <HeroMarketContextContent attributes={attributes} isPreview={isPreview} />;
      }

      case "shift-volume": {
        return <ShiftVolumeContent attributes={attributes} />;
      }

      case "morning-mvp": {
        return <MorningMvpContent attributes={attributes} />;
      }

      case "evening-all-star": {
        return <EveningAllStarContent attributes={attributes} />;
      }

      case "night-shift-legend": {
        return <NightShiftLegendContent attributes={attributes} />;
      }

      case "all-hours-champion": {
        return <AllHoursChampionContent attributes={attributes} />;
      }

      case "urgent-champion": {
        return <UrgentChampionContent attributes={attributes} />;
      }

      case "planning-ahead": {
        return <PlanningAheadContent attributes={attributes} />;
      }

      case "total-earnings": {
        return <TotalEarningsContent attributes={attributes} />;
      }

      case "total-earnings-lifetime": {
        return <TotalEarningsLifetimeContent attributes={attributes} />;
      }

      case "agent-req-diversity": {
        return <WorkerShiftTypeDiversityContent attributes={attributes} />;
      }

      case "distance-and-commute": {
        return <DistanceAndCommuteContent attributes={attributes} />;
      }

      case "top-workplaces": {
        return <TopWorkplacesContent attributes={attributes} />;
      }

      case "team-and-connections": {
        return <TeamAndConnectionsContent attributes={attributes} />;
      }

      case "community-reviews": {
        return <CommunityReviewsContent attributes={attributes} />;
      }

      case "summary": {
        return <SummaryContent attributes={attributes} isPreview={isPreview} />;
      }

      default: {
        return null;
      }
    }
  };

  return (
    <AnimatedInsightWrapper
      // Use a different id for preview to avoid duplicate IDs in the DOM
      id={isPreview ? `preview-${insight.attributes.insightType}` : undefined}
      insightType={insight.attributes.insightType}
      visible={visible}
      // In preview mode, use relative positioning with explicit dimensions
      // instead of absolute positioning with inset: 0
      sx={
        isPreview
          ? {
              position: "relative",
              inset: "unset",
              width: "100vw",
              height: "100vh",
            }
          : undefined
      }
      onClick={onClick}
    >
      {getContent()}
    </AnimatedInsightWrapper>
  );
}
